import { cn } from '@/shared/lib/cn'
import { asset } from '@/shared/lib/asset'

interface LogoProps {
  className?: string
  /** Logo mark height in px (defaults to 40). */
  size?: number
  /** Whether to show the full wordmark logo image. */
  showWordmark?: boolean
}

/**
 * Brand logo — always the real brand asset (`/issalogo.webp`).
 *
 * - `showWordmark` (default false) → renders the logo at its natural
 *   wide aspect ratio (≈1.47:1). Used in the navbar (expanded), footer,
 *   and auth panel where there's horizontal room.
 * - icon-only (`showWordmark={false}`) → the SAME real logo, fit inside
 *   a square box with `object-contain`. Used for small avatars and the
 *   sidebars / collapsed navbar. We no longer ship a separate hand-drawn
 *   SVG monogram — the brand mark is consistent everywhere.
 */
export function Logo({ className, size = 40, showWordmark = false }: LogoProps) {
  if (showWordmark) {
    return (
      <img
        src={asset('/issalogo.webp')}
        alt="Abdallah Issa"
        // Intrinsic ratio is 353×240 (≈1.47:1).
        height={Math.round(size * 1.15)}
        className={cn('w-auto select-none', className)}
        style={{ height: Math.round(size * 1.15) }}
        loading="eager"
        decoding="async"
      />
    )
  }

  // Icon-only: the real logo fit inside a square box. `object-contain`
  // keeps the full mark visible (no cropping) and the box reserves a
  // stable square footprint for avatars / sidebars.
  return (
    <span
      className={cn('relative grid shrink-0 place-items-center', className)}
      style={{ width: size, height: size }}
    >
      <span
        className="absolute inset-0 rounded-[28%] bg-primary/15 blur-md"
        aria-hidden
      />
      <img
        src={asset('/issalogo.webp')}
        alt="Abdallah Issa"
        width={size}
        height={size}
        className="relative z-10 h-full w-full select-none object-contain drop-shadow-[0_2px_8px_hsl(var(--primary)/0.35)]"
        loading="eager"
        decoding="async"
      />
    </span>
  )
}

